import React, { useState, useEffect } from 'react';
import { Loader2, Shield, ArrowRight } from 'lucide-react';
import { beginSignIn } from '../../hooks/useAuth.js';
import logoUrl from '../../assets/logo-white.png';

declare const __APP_VERSION__: string;

/**
 * Futuristic 3D login — dark glassmorphism, red accent system.
 * Palette: #0A0A0B / #16171A bg, #E10600 / #FF2A2A accent, #B8BCC4 secondary text,
 * #2A2C31 / #5A5F69 surfaces. Orbitron for display type.
 * The sign-in itself is real: Entra ID SSO via /api/v1/auth/login.
 */
export function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [logoIn, setLogoIn] = useState(false);

  // Scan-line reveal of the logo on load.
  useEffect(() => {
    const t = setTimeout(() => setLogoIn(true), 250);
    return () => clearTimeout(t);
  }, []);

  function handleEntraLogin() {
    setIsLoading(true);
    beginSignIn(); // real Entra OIDC redirect; returns with a signed session cookie
  }

  return (
    <div className="login-scene">
      {/* Ambient background: grid floor + red horizon glow */}
      <div className="login-bg" aria-hidden="true">
        <div className="login-grid" />
        <div className="login-horizon" />
        <div className="login-vignette" />
      </div>

      {/* Login card */}
      <div className="login-card">
        {/* Logo with scan-line reveal + pulsing red halo */}
        <div className={'login-logo' + (logoIn ? ' is-in' : '')}>
          <div className="login-logo-halo" aria-hidden="true" />
          <img src={logoUrl} alt="TechSys Connect" className="login-logo-img" draggable={false} />
          <div className="login-scanline" aria-hidden="true" />
        </div>

        <h1 className="login-title">TECHSYS CONNECT</h1>
        <p className="login-sub">SECURE ACCESS TERMINAL</p>

        <div className="login-divider" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>

        <p className="login-desc">
          Authenticate with your company Microsoft account to access the operations console.
        </p>

        <button
          onClick={handleEntraLogin}
          disabled={isLoading}
          className="login-btn"
          aria-label="Sign in with Microsoft"
        >
          <span className="login-btn-glow" aria-hidden="true" />
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <rect x="1" y="1" width="10" height="10" fill="#F25022" />
              <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
              <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
              <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
            </svg>
          )}
          <span className="login-btn-text">
            {isLoading ? 'AUTHENTICATING…' : 'CONTINUE WITH MICROSOFT'}
          </span>
          {!isLoading && <ArrowRight className="h-4 w-4 ml-auto opacity-60" />}
        </button>

        <div className="login-secure">
          <Shield className="h-3.5 w-3.5 shrink-0" />
          <span>Secured by Microsoft Entra ID · credentials are never stored by TechSys Connect</span>
        </div>
      </div>

      <p className="login-footer">
        TECHSYS CONNECT OPERATIONS · <span className="text-[#FF2A2A]">v{__APP_VERSION__}</span>
      </p>
    </div>
  );
}
